import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { KeyRound, Plus, Trash2 } from 'lucide-react';
import type { LiveApiKeyRow } from '@quds/shared';
import { useCan } from '@/auth/AuthContext';
import { Button, ConfirmDialog, FormField, Input, SecretField, StatusBadge } from '@/components/ui';
import {
  createBusinessApiKey,
  deactivateBusinessApiKey,
  revealBusinessApiKey,
  rotateBusinessApiKey,
} from '@/data';
import { ApiError, errorMessage } from '@/lib/api';
import { formatDate, formatDateTime } from '@/lib/format';

function newIdempotencyKey(): string {
  return crypto.randomUUID();
}

export interface BranchApiManagerProps {
  businessId: string;
  branchId: string;
  branchName: string;
  keys: LiveApiKeyRow[];
  /** When false, manage actions are disabled (Production unavailable). */
  productionAvailable: boolean;
  /** Called after create/rotate/remove so the parent can reload details (background). */
  onChanged?: () => void;
  /** Scroll/highlight this panel (e.g. from Branches → API action). */
  focused?: boolean;
  className?: string;
  /** Optional id override for deep-link focus. */
  id?: string;
}

type LocalKeyPatch = Partial<LiveApiKeyRow> & { id: string };

/**
 * Per-branch API key manager. Keys are always branch-scoped.
 * Shared by Access & API and Actions → API & Integration.
 */
export function BranchApiManager({
  businessId,
  branchId,
  branchName,
  keys: keysProp,
  productionAvailable,
  onChanged,
  focused,
  className,
  id,
}: BranchApiManagerProps) {
  const canManage = useCan()('api.manage');
  const panelId = id ?? `branch-api-${branchId}`;
  const rootRef = useRef<HTMLDivElement>(null);

  /** Stable for one logical first-generate until that op settles successfully or is abandoned. */
  const firstGenerateIdempotencyRef = useRef<string | null>(null);
  const generateInFlightRef = useRef(false);

  const [generating, setGenerating] = useState(false);
  const [generateError, setGenerateError] = useState<string | null>(null);
  const [successBanner, setSuccessBanner] = useState<string | null>(null);

  /** Optimistic / mutation-driven key list — UI does not wait on parent refetch. */
  const [localKeys, setLocalKeys] = useState<LiveApiKeyRow[] | null>(null);
  const [removedIds, setRemovedIds] = useState<Set<string>>(() => new Set());

  const [confirmAddAnother, setConfirmAddAnother] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [addLabel, setAddLabel] = useState('');

  const [revealedByKey, setRevealedByKey] = useState<Record<string, string>>({});
  const [revealMessageByKey, setRevealMessageByKey] = useState<Record<string, string>>({});
  const [recoverableByKey, setRecoverableByKey] = useState<Record<string, boolean>>({});
  const [revealBusyId, setRevealBusyId] = useState<string | null>(null);

  const [rotateTarget, setRotateTarget] = useState<LiveApiKeyRow | null>(null);
  const [rotating, setRotating] = useState(false);
  const [rotateError, setRotateError] = useState<string | null>(null);

  const [removeTarget, setRemoveTarget] = useState<LiveApiKeyRow | null>(null);
  const [removing, setRemoving] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);

  const addLabelId = useId();

  useEffect(() => {
    if (!focused || !rootRef.current) return;
    rootRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [focused, branchId]);

  // Reconcile server props into local optimistic state (never drop one-time secrets).
  useEffect(() => {
    setLocalKeys((prev) => {
      if (!prev) return keysProp;
      const byId = new Map<string, LiveApiKeyRow>();
      for (const k of keysProp) byId.set(k.id, k);
      for (const k of prev) {
        if (!byId.has(k.id) && !removedIds.has(k.id)) byId.set(k.id, k);
      }
      return [...byId.values()];
    });
  }, [keysProp, removedIds]);

  const keys = useMemo(() => {
    const base = localKeys ?? keysProp;
    return base.filter((k) => !removedIds.has(k.id));
  }, [localKeys, keysProp, removedIds]);

  const activeKeys = useMemo(() => keys.filter((k) => k.status === 'active'), [keys]);
  const sortedKeys = useMemo(
    () =>
      [...keys].sort((a, b) => {
        if (a.status !== b.status) return a.status === 'active' ? -1 : 1;
        return b.createdAt.localeCompare(a.createdAt);
      }),
    [keys],
  );

  const configured = activeKeys.length > 0;

  const clearReveal = (keyId: string) => {
    setRevealedByKey((prev) => {
      const next = { ...prev };
      delete next[keyId];
      return next;
    });
    setRevealMessageByKey((prev) => {
      const next = { ...prev };
      delete next[keyId];
      return next;
    });
  };

  const upsertLocalKey = (patch: LocalKeyPatch) => {
    setLocalKeys((prev) => {
      const base = prev ?? keysProp;
      const idx = base.findIndex((k) => k.id === patch.id);
      if (idx === -1) {
        const row: LiveApiKeyRow = {
          id: patch.id,
          branchId,
          label: patch.label ?? 'Branch POS API',
          enrollmentCodeRedacted: true,
          status: patch.status ?? 'active',
          createdAt: patch.createdAt ?? new Date().toISOString(),
          lastUsedAt: patch.lastUsedAt ?? null,
          display: patch.display ?? 'qpos_••••••••',
        };
        return [row, ...base];
      }
      const next = [...base];
      const cur = next[idx]!;
      next[idx] = {
        ...cur,
        label: patch.label !== undefined ? patch.label : cur.label,
        status: patch.status ?? cur.status,
        createdAt: patch.createdAt ?? cur.createdAt,
        lastUsedAt: patch.lastUsedAt !== undefined ? patch.lastUsedAt : cur.lastUsedAt,
        display: patch.display ?? cur.display,
      };
      return next;
    });
  };

  const generate = async (opts: { intent: 'first' | 'additional'; label?: string }) => {
    if (generateInFlightRef.current || generating || !canManage || !productionAvailable) return;
    if (opts.intent === 'first' && activeKeys.length > 0) return;

    generateInFlightRef.current = true;
    setGenerating(true);
    setGenerateError(null);
    setSuccessBanner(null);

    let idem: string;
    if (opts.intent === 'first') {
      if (!firstGenerateIdempotencyRef.current) {
        firstGenerateIdempotencyRef.current = newIdempotencyKey();
      }
      idem = firstGenerateIdempotencyRef.current;
    } else {
      idem = newIdempotencyKey();
    }

    try {
      const res = await createBusinessApiKey(businessId, branchId, {
        label: opts.label?.trim() || undefined,
        idempotencyKey: idem,
        intent: opts.intent,
      });

      if (opts.intent === 'first') {
        firstGenerateIdempotencyRef.current = null;
      }

      upsertLocalKey({
        id: res.apiKeyId,
        branchId,
        label: res.label ?? (opts.label?.trim() || 'Branch POS API'),
        status: 'active',
        createdAt: new Date().toISOString(),
        display: 'qpos_••••••••',
      });

      if (res.apiKeyPlaintext) {
        setRevealedByKey((prev) => ({ ...prev, [res.apiKeyId]: res.apiKeyPlaintext! }));
        setRecoverableByKey((prev) => ({ ...prev, [res.apiKeyId]: true }));
      }

      setSuccessBanner(
        opts.intent === 'first' ? 'API Generated Successfully' : 'Additional API key created successfully',
      );
      setConfirmAddAnother(false);
      setShowAddForm(false);
      setAddLabel('');
      onChanged?.();
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        setGenerateError(e.message || 'This branch already has an active API key. Refreshing…');
        firstGenerateIdempotencyRef.current = null;
        onChanged?.();
      } else {
        setGenerateError(errorMessage(e));
      }
    } finally {
      generateInFlightRef.current = false;
      setGenerating(false);
    }
  };

  const revealKey = async (key: LiveApiKeyRow) => {
    if (!canManage || !productionAvailable) return;
    setRevealBusyId(key.id);
    setRevealMessageByKey((prev) => {
      const next = { ...prev };
      delete next[key.id];
      return next;
    });
    try {
      const res = await revealBusinessApiKey(businessId, branchId, key.id, {
        idempotencyKey: newIdempotencyKey(),
      });
      if (res.recoverable && res.apiKeyPlaintext) {
        setRevealedByKey((prev) => ({ ...prev, [key.id]: res.apiKeyPlaintext! }));
        setRecoverableByKey((prev) => ({ ...prev, [key.id]: true }));
      } else {
        clearReveal(key.id);
        setRecoverableByKey((prev) => ({ ...prev, [key.id]: false }));
        setRevealMessageByKey((prev) => ({
          ...prev,
          [key.id]: 'Not Recoverable — this key has no recovery copy. Rotate to issue a new recoverable key.',
        }));
      }
    } catch (e) {
      setRevealMessageByKey((prev) => ({
        ...prev,
        [key.id]: errorMessage(e),
      }));
    } finally {
      setRevealBusyId(null);
    }
  };

  const submitRotate = async () => {
    if (!rotateTarget || rotating || !canManage) return;
    setRotating(true);
    setRotateError(null);
    try {
      const res = await rotateBusinessApiKey(businessId, branchId, rotateTarget.id, {
        idempotencyKey: newIdempotencyKey(),
      });
      clearReveal(rotateTarget.id);
      setRemovedIds((prev) => new Set(prev).add(rotateTarget.id));
      upsertLocalKey({
        id: res.apiKeyId,
        branchId,
        label: res.label ?? rotateTarget.label,
        status: 'active',
        createdAt: new Date().toISOString(),
        display: 'qpos_••••••••',
      });
      setRecoverableByKey((prev) => {
        const next = { ...prev };
        delete next[rotateTarget.id];
        if (res.apiKeyId) next[res.apiKeyId] = true;
        return next;
      });
      if (res.apiKeyPlaintext && res.apiKeyId) {
        setRevealedByKey((prev) => ({ ...prev, [res.apiKeyId]: res.apiKeyPlaintext! }));
      }
      setSuccessBanner('API key rotated successfully');
      setRotateTarget(null);
      onChanged?.();
    } catch (e) {
      setRotateError(errorMessage(e));
    } finally {
      setRotating(false);
    }
  };

  const submitRemove = async () => {
    if (!removeTarget || removing || !canManage) return;
    setRemoving(true);
    setRemoveError(null);
    const targetId = removeTarget.id;
    try {
      await deactivateBusinessApiKey(businessId, branchId, targetId, {
        idempotencyKey: newIdempotencyKey(),
      });
      clearReveal(targetId);
      setRemovedIds((prev) => new Set(prev).add(targetId));
      setLocalKeys((prev) => (prev ?? keysProp).map((k) => (k.id === targetId ? { ...k, status: 'inactive' as const } : k)));
      setSuccessBanner('API key removed successfully');
      setRemoveTarget(null);
      onChanged?.();
    } catch (e) {
      setRemoveError(errorMessage(e));
      // Do not optimistically remove — keep target visible
    } finally {
      setRemoving(false);
    }
  };

  const isLastActive = removeTarget != null && activeKeys.length === 1 && removeTarget.status === 'active';
  const orphanSecrets = Object.entries(revealedByKey).filter(([keyId]) => !keys.some((k) => k.id === keyId));

  return (
    <div
      ref={rootRef}
      id={panelId}
      className={[
        'rounded-md border border-line bg-surface px-3 py-3',
        focused ? 'ring-2 ring-primary/30' : '',
        className ?? '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <KeyRound size={14} aria-hidden="true" className="text-ink-muted" />
        <p className="text-sm font-medium text-ink">{branchName}</p>
        <StatusBadge
          tone={configured ? 'success' : 'neutral'}
          label={configured ? 'API Configured' : 'API Not Configured'}
          dot={false}
        />
        {activeKeys.length > 0 && (
          <span className="text-xs text-ink-muted">
            {activeKeys.length} active key{activeKeys.length === 1 ? '' : 's'}
          </span>
        )}
      </div>

      {successBanner && (
        <p role="status" className="mb-3 rounded-md border border-success/30 bg-success-soft px-2.5 py-1.5 text-sm text-success">
          {successBanner}
        </p>
      )}

      {activeKeys.length === 0 && orphanSecrets.length === 0 ? (
        <div className="space-y-3">
          <p className="text-sm text-ink-secondary">No API key generated</p>
          {canManage ? (
            <Button
              variant="primary"
              size="sm"
              loading={generating}
              disabled={!productionAvailable || generating}
              onClick={() => void generate({ intent: 'first' })}
            >
              Generate API
            </Button>
          ) : (
            <p className="text-[13px] text-ink-muted">View only — your role cannot manage API keys.</p>
          )}
          {generateError && <p className="text-sm text-danger">{generateError}</p>}
          {!productionAvailable && canManage && (
            <p className="text-[13px] text-warning">Production temporarily unavailable — generate disabled.</p>
          )}
          <p className="text-[12px] text-ink-muted">Copy this API into the Point of Sale for this branch.</p>
        </div>
      ) : null}

      {activeKeys.length === 0 &&
        orphanSecrets.map(([keyId, plaintext]) => (
          <div key={keyId} className="space-y-2">
            <p className="text-sm font-medium text-success">API Generated Successfully</p>
            <SecretField
              label="API KEY"
              value={plaintext}
              revealed
              maskedPlaceholder="qpos_••••••••"
              onHide={() => clearReveal(keyId)}
            />
          </div>
        ))}

      {activeKeys.length > 0 && (
        <ul className="max-h-[28rem] space-y-2 overflow-y-auto pr-0.5">
          {sortedKeys
            .filter((k) => k.status === 'active')
            .map((k) => {
              const revealed = revealedByKey[k.id];
              const message = revealMessageByKey[k.id];
              const recoverable = recoverableByKey[k.id];
              return (
                <li key={k.id} className="rounded-md border border-line/80 px-2.5 py-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium text-ink">{k.label?.trim() || 'Unlabeled key'}</span>
                    <StatusBadge tone="success" label="active" />
                    {recoverable === true && <StatusBadge tone="info" label="Recoverable" dot={false} />}
                    {recoverable === false && <StatusBadge tone="warning" label="Not Recoverable" dot={false} />}
                    <span className="font-mono text-[11px] text-ink-muted" title={k.id}>
                      {k.id.slice(0, 8)}…
                    </span>
                  </div>
                  <p className="mt-0.5 text-xs text-ink-secondary">
                    {k.display} · Created {formatDate(k.createdAt)}
                    {k.lastUsedAt ? ` · Last used ${formatDateTime(k.lastUsedAt)}` : ''}
                  </p>
                  {revealed && <p className="mt-1 text-[12px] font-medium text-success">API KEY — copy now</p>}
                  {canManage ? (
                    <div className="mt-2 space-y-2">
                      <SecretField
                        label="API KEY"
                        value={revealed ?? null}
                        maskedPlaceholder="qpos_••••••••"
                        revealed={!!revealed}
                        revealLoading={revealBusyId === k.id}
                        message={message}
                        disabled={!productionAvailable}
                        onReveal={() => void revealKey(k)}
                        onHide={() => clearReveal(k.id)}
                        onRotate={
                          productionAvailable
                            ? () => {
                                setRotateError(null);
                                setRotateTarget(k);
                              }
                            : undefined
                        }
                      />
                      <Button
                        variant="danger"
                        size="sm"
                        leftIcon={<Trash2 size={14} aria-hidden="true" />}
                        disabled={!productionAvailable || removing}
                        onClick={() => {
                          setRemoveError(null);
                          setRemoveTarget(k);
                        }}
                      >
                        Remove API
                      </Button>
                    </div>
                  ) : (
                    <p className="mt-2 text-[13px] text-ink-muted">
                      Metadata only — reveal, rotate, and remove require api.manage.
                    </p>
                  )}
                </li>
              );
            })}
        </ul>
      )}

      {canManage && activeKeys.length > 0 && (
        <div className="mt-3 border-t border-line pt-3">
          {!showAddForm ? (
            <Button
              variant="ghost"
              size="sm"
              leftIcon={<Plus size={14} aria-hidden="true" />}
              disabled={!productionAvailable || generating}
              onClick={() => {
                setGenerateError(null);
                setConfirmAddAnother(true);
              }}
            >
              Add Another API Key
            </Button>
          ) : (
            <div className="space-y-2">
              <p className="text-[13px] text-ink-secondary">
                Creates an <span className="font-medium text-ink">additional active key</span> for this branch only.
                Existing keys stay active until removed or rotated.
              </p>
              <FormField label="Label (optional)" htmlFor={addLabelId}>
                <Input
                  id={addLabelId}
                  value={addLabel}
                  onChange={(e) => setAddLabel(e.target.value)}
                  maxLength={120}
                  disabled={generating}
                />
              </FormField>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="primary"
                  size="sm"
                  loading={generating}
                  disabled={!productionAvailable || generating}
                  onClick={() => void generate({ intent: 'additional', label: addLabel })}
                >
                  Create Additional API Key
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={generating}
                  onClick={() => {
                    setShowAddForm(false);
                    setAddLabel('');
                  }}
                >
                  Cancel
                </Button>
              </div>
              {generateError && <p className="text-sm text-danger">{generateError}</p>}
            </div>
          )}
        </div>
      )}

      <ConfirmDialog
        open={confirmAddAnother}
        title="Add another API key?"
        message={
          <div className="space-y-2">
            <p>This branch already has an active API key.</p>
            <p>Creating another API key will keep the existing key active.</p>
          </div>
        }
        confirmLabel="Create Additional API Key"
        tone="danger"
        onConfirm={() => {
          setConfirmAddAnother(false);
          setShowAddForm(true);
        }}
        onCancel={() => setConfirmAddAnother(false)}
      />

      <ConfirmDialog
        open={!!rotateTarget}
        title="Rotate API key"
        message={
          <div className="space-y-2">
            <p>
              Rotate the key for <span className="font-medium text-ink">{branchName}</span> only. Other branches are
              unaffected.
            </p>
            <p>The previous key will be deactivated. POS terminals using it must be updated with the new key.</p>
            {rotateError && <p className="text-danger">{rotateError}</p>}
          </div>
        }
        confirmLabel={rotating ? 'Rotating…' : 'Rotate key'}
        tone="danger"
        loading={rotating}
        onConfirm={() => void submitRotate()}
        onCancel={() => {
          if (!rotating) {
            setRotateTarget(null);
            setRotateError(null);
          }
        }}
      />

      <ConfirmDialog
        open={!!removeTarget}
        title="Remove this API key?"
        message={
          <div className="space-y-2">
            <p>This API key will stop working immediately.</p>
            <p>Any POS currently using this specific API key will lose Cloud POS access.</p>
            <p>Other API keys for this branch will remain active.</p>
            {isLastActive && (
              <p className="rounded-md border border-warning/40 bg-warning-soft px-2.5 py-1.5 text-warning">
                This is the last active API key for this branch. Removing it will leave this branch without a Cloud POS
                API credential.
              </p>
            )}
            {removeError && <p className="text-danger">{removeError}</p>}
          </div>
        }
        confirmLabel={removing ? 'Removing…' : 'Remove API'}
        tone="danger"
        loading={removing}
        onConfirm={() => void submitRemove()}
        onCancel={() => {
          if (!removing) {
            setRemoveTarget(null);
            setRemoveError(null);
          }
        }}
      />
    </div>
  );
}

/** Count of Production branches that have at least one API key (any status). */
export function countConfiguredBranches(branchIds: string[], keys: LiveApiKeyRow[]): number {
  const withKeys = new Set(keys.map((k) => k.branchId));
  return branchIds.filter((id) => withKeys.has(id)).length;
}

export function keysForBranch(keys: LiveApiKeyRow[], branchId: string): LiveApiKeyRow[] {
  return keys.filter((k) => k.branchId === branchId);
}
